import { z } from "zod";
import type { AssetFileKind } from "./enums.js";

/**
 * Dossier (docs/DOSSIER.md): documenten van een gebouw met een soort, versies, een geldig-tot-datum en
 * koppelingen aan ruimtes en assets. De rubrieken liggen vast (besluit 24-10-2026); de soorten daaronder
 * beheert de organisatie zelf.
 */

export const DOC_CATEGORIES = [
  "vergunningen",
  "veiligheid",
  "energie",
  "technisch",
  "eigendom",
  "installaties",
  "overig",
] as const;
export type DocCategory = (typeof DOC_CATEGORIES)[number];
export const DOC_CATEGORY_LABELS: Record<DocCategory, string> = {
  vergunningen: "Vergunningen",
  veiligheid: "Veiligheid",
  energie: "Energie",
  technisch: "Technisch",
  eigendom: "Eigendom en contracten",
  installaties: "Installaties",
  overig: "Overig",
};

export const DOC_VISIBILITIES = ["intern", "klant", "vertrouwelijk"] as const;
export type DocVisibility = (typeof DOC_VISIBILITIES)[number];
export const DOC_VISIBILITY_LABELS: Record<DocVisibility, string> = {
  intern: "Intern",
  klant: "Klant ziet mee",
  vertrouwelijk: "Vertrouwelijk",
};
export const DOC_VISIBILITY_HINTS: Record<DocVisibility, string> = {
  intern: "Iedereen met toegang tot dit gebouw.",
  klant: "Ook medewerkers van de klant met toegang tot dit gebouw.",
  vertrouwelijk: "Alleen eigenaren, beheerders en de verantwoordelijke. Openen wordt gelogd.",
};

/** Vakgebieden van tekeningen; het voorstel volgt uit de NL-SfB-code van de asset. */
export const DRAWING_DISCIPLINES = [
  "W",
  "E",
  "Brand",
  "Sprinkler",
  "Lift",
  "Bouwkundig",
  "Constructie",
  "Overig",
] as const;
export type DrawingDiscipline = (typeof DRAWING_DISCIPLINES)[number];

export function disciplineFor(elementCode: string | null | undefined): DrawingDiscipline | null {
  if (!elementCode) return null;
  if (elementCode.startsWith("66")) return "Lift";
  if (elementCode.startsWith("55") || elementCode.startsWith("65")) return "Brand";
  if (elementCode.startsWith("5")) return "W";
  if (elementCode.startsWith("6")) return "E";
  if (elementCode.startsWith("2")) return "Constructie";
  if (/^[1-4]/.test(elementCode)) return "Bouwkundig";
  return null;
}

/** Een document dat binnen zoveel dagen verloopt (als de soort niets zegt), krijgt "verloopt binnenkort". */
export const DOC_DEFAULT_REMIND_DAYS = 90;
/** Zo lang blijft een verwijderd document in de prullenbak. */
export const DOC_TRASH_DAYS = 30;
export const DOC_MAX_BYTES = 100 * 1024 * 1024;

export const DOC_MIMES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
  "image/vnd.dwg",
  "application/acad",
  "application/x-dwg",
  "image/vnd.dxf",
  "application/zip",
  "application/x-zip-compressed",
  "application/x-step",
  "model/ifc",
] as const;
export const DOC_MIMES_LABEL = "PDF, afbeeldingen, Word, Excel, tekst, DWG/DXF, IFC of ZIP";

/** MIME-type uit de extensie, voor bestanden waar de browser er geen geeft (DWG, IFC). */
export function docMimeOf(name: string, browserType: string): string {
  const ext = name.toLowerCase().split(".").pop() ?? "";
  const byExt: Record<string, string> = {
    dwg: "image/vnd.dwg",
    dxf: "image/vnd.dxf",
    ifc: "model/ifc",
    zip: "application/zip",
    pdf: "application/pdf",
  };
  if (browserType && (DOC_MIMES as readonly string[]).includes(browserType)) return browserType;
  return byExt[ext] ?? browserType;
}

// ---------- soorten ----------

export interface DefaultDocType {
  key: string;
  category: DocCategory;
  name: string;
  validityMonths: number | null;
  remindDays: number | null;
  visibility: DocVisibility;
  confidential?: boolean;
  drawing?: boolean;
  /** Woorden in een bestandsnaam die bij deze soort horen (voorstel bij uploaden). */
  words: string[];
}

/**
 * Standaardlijst voor een nieuwe organisatie (docs/DOSSIER.md §3). De geldigheden zijn een voorstel; de
 * organisatie past ze aan bij Organisatie › Documentsoorten.
 */
export const DEFAULT_DOC_TYPES: DefaultDocType[] = [
  {
    key: "omgevingsvergunning",
    category: "vergunningen",
    name: "Omgevingsvergunning",
    validityMonths: null,
    remindDays: null,
    visibility: "intern",
    words: ["omgevingsvergunning", "bouwvergunning"],
  },
  {
    key: "brandveilig_gebruik",
    category: "vergunningen",
    name: "Vergunning brandveilig gebruik of gebruiksmelding",
    validityMonths: null,
    remindDays: null,
    visibility: "klant",
    words: ["gebruiksmelding", "brandveilig", "gebruiksvergunning"],
  },
  {
    key: "leegstandvergunning",
    category: "vergunningen",
    name: "Leegstandvergunning",
    validityMonths: 24,
    remindDays: 90,
    visibility: "intern",
    words: ["leegstand"],
  },
  {
    key: "ontruimingsplan",
    category: "veiligheid",
    name: "Ontruimingsplan",
    validityMonths: 12,
    remindDays: 60,
    visibility: "klant",
    words: ["ontruim"],
  },
  {
    key: "bhv_plan",
    category: "veiligheid",
    name: "BHV-plan",
    validityMonths: 12,
    remindDays: 60,
    visibility: "intern",
    words: ["bhv"],
  },
  {
    key: "rie",
    category: "veiligheid",
    name: "RI&E",
    validityMonths: 48,
    remindDays: 90,
    visibility: "intern",
    words: ["rie", "ri&e", "risico-inventarisatie"],
  },
  {
    key: "asbest",
    category: "veiligheid",
    name: "Asbestinventarisatie",
    validityMonths: null,
    remindDays: null,
    visibility: "intern",
    words: ["asbest"],
  },
  {
    key: "legionella",
    category: "veiligheid",
    name: "Legionellarisicoanalyse en beheersplan",
    validityMonths: 36,
    remindDays: 90,
    visibility: "intern",
    words: ["legionella", "lra"],
  },
  {
    key: "energielabel",
    category: "energie",
    name: "Energielabel",
    validityMonths: 120,
    remindDays: 120,
    visibility: "klant",
    words: ["energielabel", "ep-online", "epa"],
  },
  {
    key: "informatieplicht",
    category: "energie",
    name: "Informatieplicht energiebesparing",
    validityMonths: 48,
    remindDays: 90,
    visibility: "intern",
    words: ["informatieplicht", "eed"],
  },
  {
    key: "maatwerkadvies",
    category: "energie",
    name: "Maatwerkadvies",
    validityMonths: null,
    remindDays: null,
    visibility: "intern",
    words: ["maatwerkadvies"],
  },
  {
    key: "drawing",
    category: "technisch",
    name: "Tekening",
    validityMonths: null,
    remindDays: null,
    visibility: "intern",
    drawing: true,
    words: ["tekening", "rev", "schema", ".dwg", ".dxf"],
  },
  {
    key: "opleverdossier",
    category: "technisch",
    name: "Opleverdossier",
    validityMonths: null,
    remindDays: null,
    visibility: "intern",
    words: ["oplever"],
  },
  {
    key: "rapport",
    category: "technisch",
    name: "Rapport of onderzoek",
    validityMonths: null,
    remindDays: null,
    visibility: "intern",
    words: ["rapport", "onderzoek", "mjop", "conditie"],
  },
  {
    key: "eigendom",
    category: "eigendom",
    name: "Eigendomsbewijs of erfpacht",
    validityMonths: null,
    remindDays: null,
    visibility: "intern",
    confidential: true,
    words: ["eigendom", "erfpacht", "akte"],
  },
  {
    key: "verzekering",
    category: "eigendom",
    name: "Opstalverzekering",
    validityMonths: 12,
    remindDays: 60,
    visibility: "intern",
    words: ["opstal", "verzekering", "polis"],
  },
  {
    key: "huurovereenkomst",
    category: "eigendom",
    name: "Huurovereenkomst",
    validityMonths: null,
    remindDays: 180,
    visibility: "intern",
    confidential: true,
    words: ["huur"],
  },
  {
    key: "contract",
    category: "eigendom",
    name: "Onderhoudscontract",
    validityMonths: null,
    remindDays: 90,
    visibility: "intern",
    words: ["contract", "raamovereenkomst"],
  },
  {
    key: "manual",
    category: "installaties",
    name: "Handleiding",
    validityMonths: null,
    remindDays: null,
    visibility: "intern",
    words: ["handleiding", "manual", "gebruiksaanwijzing", "installatievoorschrift"],
  },
  {
    key: "certificaat",
    category: "installaties",
    name: "Certificaat of verklaring",
    validityMonths: null,
    remindDays: null,
    visibility: "intern",
    words: ["certificaat", "verklaring", "keuring"],
  },
  {
    key: "document",
    category: "overig",
    name: "Overig document",
    validityMonths: null,
    remindDays: null,
    visibility: "intern",
    words: [],
  },
];

/** Soort bij een bijlage van een asset (Documenten, Tekeningen, Handleidingen). */
export const ASSET_FILE_KIND_TYPE: Record<AssetFileKind, string> = {
  document: "document",
  drawing: "drawing",
  manual: "manual",
};

export interface DocumentTypeDto {
  id: string;
  key: string | null;
  category: DocCategory;
  name: string;
  validityMonths: number | null;
  remindDays: number | null;
  visibility: DocVisibility;
  confidential: boolean;
  drawing: boolean;
  active: boolean;
  /** Aantal documenten van deze soort (niet in de prullenbak). */
  documentCount: number;
}

/** Tekeningnummer en revisie uit een bestandsnaam, zoals `W455_W-101_revC.pdf` of `E-001 rev D.dwg`. */
export function parseDrawingName(name: string): { number: string; revision: string | null } | null {
  const base = name.replace(/\.[a-z0-9]{2,5}$/i, "");
  const num = /(?:^|[\s_])([A-Z]{1,3}-?\d{2,4}(?:\.\d+)?)(?=$|[\s_-])/i.exec(base);
  if (!num) return null;
  const rev = /(?:^|[\s_-])rev(?:isie)?[\s_.-]?([A-Z0-9]{1,3})(?=$|[\s_.-])/i.exec(base);
  return { number: num[1]!.toUpperCase(), revision: rev ? rev[1]!.toUpperCase() : null };
}

/** Voorstel voor de soort op basis van de bestandsnaam: eerste soort met een woord dat erin staat. */
export function suggestDocType<T extends { id: string; key: string | null; active: boolean; drawing: boolean }>(
  fileName: string,
  types: T[],
): T | null {
  const low = fileName.toLowerCase();
  const active = types.filter((t) => t.active);
  if (parseDrawingName(fileName)?.revision) {
    const d = active.find((t) => t.drawing);
    if (d) return d;
  }
  for (const def of DEFAULT_DOC_TYPES) {
    if (!def.words.some((w) => low.includes(w))) continue;
    const t = active.find((x) => x.key === def.key);
    if (t) return t;
  }
  return null;
}

// ---------- status ----------

export const DOC_STATUSES = ["ok", "expiring", "expired"] as const;
export type DocStatus = (typeof DOC_STATUSES)[number];
export const DOC_STATUS_LABELS: Record<DocStatus, string> = {
  ok: "Geldig",
  expiring: "Verloopt binnenkort",
  expired: "Verlopen",
};

/** Status van een document uit de geldig-tot-datum van de nieuwste versie. */
export function docStatus(validUntil: string | null, remindDays: number | null, today: string): DocStatus {
  if (!validUntil) return "ok";
  if (validUntil < today) return "expired";
  const warn = new Date(`${today}T00:00:00Z`);
  warn.setUTCDate(warn.getUTCDate() + (remindDays ?? DOC_DEFAULT_REMIND_DAYS));
  return validUntil <= warn.toISOString().slice(0, 10) ? "expiring" : "ok";
}

/** Datum + aantal maanden (voorstel voor geldig tot bij uploaden). */
export function addMonthsIso(iso: string, months: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  const day = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + months);
  const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(day, last));
  return d.toISOString().slice(0, 10);
}

// ---------- invoer ----------

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Datum als JJJJ-MM-DD");
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v ? v : null));

export const documentTypeBodySchema = z.object({
  category: z.enum(DOC_CATEGORIES),
  name: z.string().trim().min(1, "Naam is verplicht").max(120),
  validityMonths: z.number().int().min(1).max(600).nullable(),
  remindDays: z.number().int().min(1).max(730).nullable(),
  visibility: z.enum(DOC_VISIBILITIES),
  confidential: z.boolean(),
  drawing: z.boolean(),
  active: z.boolean(),
});
export type DocumentTypeBody = z.infer<typeof documentTypeBodySchema>;

export const documentLinksSchema = z.object({
  assetIds: z.array(z.uuid()).max(200),
  roomIds: z.array(z.uuid()).max(200),
});

const documentFields = {
  typeId: z.uuid(),
  title: z.string().trim().min(1, "Titel is verplicht").max(200),
  reference: optionalText(120),
  drawingNumber: optionalText(40),
  discipline: z.enum(DRAWING_DISCIPLINES).nullish(),
  floorId: z.uuid().nullish(),
  responsibleId: z.uuid().nullish(),
  issuer: optionalText(200),
  visibility: z.enum(DOC_VISIBILITIES).optional(),
  offline: z.boolean().optional(),
  notes: optionalText(2000),
};

export const documentVersionBodySchema = z.object({
  fileId: z.uuid(),
  issuedOn: isoDate.nullish(),
  validUntil: isoDate.nullish(),
  note: optionalText(500),
  sha256: z
    .string()
    .regex(/^[0-9a-f]{64}$/)
    .nullish(),
  revision: optionalText(20),
});
export type DocumentVersionBody = z.infer<typeof documentVersionBodySchema>;

export const documentCreateSchema = z.object({
  id: z.uuid(),
  ...documentFields,
  version: documentVersionBodySchema,
  links: documentLinksSchema.optional(),
});
export type DocumentCreateBody = z.input<typeof documentCreateSchema>;

export const documentPatchSchema = z.object(documentFields).partial();
export type DocumentPatchBody = z.input<typeof documentPatchSchema>;

// ---------- antwoorden ----------

export interface DocumentVersionDto {
  id: string;
  version: number;
  fileId: string;
  fileName: string;
  mime: string;
  size: number | null;
  issuedOn: string | null;
  validUntil: string | null;
  revision: string | null;
  note: string | null;
  /** Markeringen op deze versie (ingetekende wijzigingen, nog geen nieuwe revisie). */
  annotationCount: number;
  createdBy: string | null;
  createdAt: string;
}

export interface DocumentLinkDto {
  kind: "asset" | "room";
  id: string;
  code: string | null;
  name: string;
  /** Asset met onderdelen: de tekening geldt ook voor de onderdelen. */
  hasParts?: boolean;
}

export interface DocumentDto {
  id: string;
  buildingId: string;
  type: {
    id: string;
    key: string | null;
    name: string;
    category: DocCategory;
    drawing: boolean;
    remindDays: number | null;
  };
  title: string;
  reference: string | null;
  drawingNumber: string | null;
  revision: string | null;
  discipline: DrawingDiscipline | null;
  floor: { id: string; name: string } | null;
  responsible: { id: string; name: string } | null;
  issuer: string | null;
  visibility: DocVisibility;
  offline: boolean;
  notes: string | null;
  status: DocStatus;
  current: DocumentVersionDto;
  versionCount: number;
  links: DocumentLinkDto[];
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  deletedBy: string | null;
}

export interface DocumentDetailDto extends DocumentDto {
  versions: DocumentVersionDto[];
  canEdit: boolean;
}

/** Wat het dossier toont maar elders beheerd wordt: certificaten uit keuringen en plattegronden. */
export interface DossierCertificateDto {
  recordId: string;
  /** null = keuring of onderhoud bij het gebouw zelf. */
  asset: { id: string; code: string; name: string } | null;
  obligation: string;
  performedOn: string;
  nextDueOn: string | null;
  fileId: string;
  fileName: string;
}
export interface DossierPlanDto {
  floorId: string;
  floorName: string;
  fileId: string;
  fileName: string;
}

export interface DossierDto {
  documents: DocumentDto[];
  certificates: DossierCertificateDto[];
  plans: DossierPlanDto[];
  /** Gebruiksfuncties van het gebouw en de documenten die daarbij horen (D2). */
  usageFunctions: UsageFunction[];
  requirements: DossierRequirementDto[];
  trashCount: number;
  canEdit: boolean;
  canDelete: boolean;
}

// ---------- D2: gebruiksfunctie en verplichte documenten ----------

/** Gebruiksfuncties uit het Bbl/BAG, plus leegstand. Een gebouw kan er meer hebben. */
export const USAGE_FUNCTIONS = [
  "wonen",
  "bijeenkomst",
  "cel",
  "gezondheidszorg",
  "industrie",
  "kantoor",
  "logies",
  "onderwijs",
  "sport",
  "winkel",
  "overig",
  "leegstand",
] as const;
export type UsageFunction = (typeof USAGE_FUNCTIONS)[number];
export const USAGE_FUNCTION_LABELS: Record<UsageFunction, string> = {
  wonen: "Wonen",
  bijeenkomst: "Bijeenkomst",
  cel: "Cel",
  gezondheidszorg: "Gezondheidszorg",
  industrie: "Industrie",
  kantoor: "Kantoor",
  logies: "Logies",
  onderwijs: "Onderwijs",
  sport: "Sport",
  winkel: "Winkel",
  overig: "Overig",
  leegstand: "Leegstand",
};

/**
 * Documenten die bij een gebruiksfunctie horen (voorstel; per gebouw aan te passen met "niet van toepassing" of
 * "toch verplicht"). Sleutels van DEFAULT_DOC_TYPES.
 */
export const REQUIRED_DOC_TYPES: Record<UsageFunction, readonly string[]> = {
  wonen: ["energielabel"],
  bijeenkomst: ["energielabel", "brandveilig_gebruik", "ontruimingsplan", "bhv_plan"],
  cel: ["brandveilig_gebruik", "ontruimingsplan", "bhv_plan", "legionella"],
  gezondheidszorg: ["energielabel", "brandveilig_gebruik", "ontruimingsplan", "bhv_plan", "legionella"],
  industrie: ["informatieplicht"],
  kantoor: ["energielabel", "informatieplicht", "ontruimingsplan"],
  logies: ["energielabel", "brandveilig_gebruik", "ontruimingsplan", "legionella"],
  onderwijs: ["energielabel", "brandveilig_gebruik", "ontruimingsplan", "bhv_plan", "informatieplicht"],
  sport: ["energielabel", "brandveilig_gebruik", "legionella"],
  winkel: ["energielabel", "informatieplicht"],
  overig: [],
  leegstand: ["leegstandvergunning"],
};

export const usageFunctionsSchema = z.array(z.enum(USAGE_FUNCTIONS)).max(USAGE_FUNCTIONS.length);

export const REQUIREMENT_STATES = ["missing", "ok", "expiring", "expired", "not_applicable"] as const;
export type RequirementState = (typeof REQUIREMENT_STATES)[number];
export const REQUIREMENT_STATE_LABELS: Record<RequirementState, string> = {
  missing: "Ontbreekt",
  ok: "Aanwezig",
  expiring: "Verloopt binnenkort",
  expired: "Verlopen",
  not_applicable: "Niet van toepassing",
};

export interface DossierRequirementDto {
  type: { id: string; key: string | null; name: string; category: DocCategory };
  /** Uit de gebruiksfunctie(s), of met de hand verplicht gemaakt voor dit gebouw. */
  source: "function" | "manual";
  functions: UsageFunction[];
  state: RequirementState;
  /** Nieuwste document van deze soort, als er een is. */
  document: { id: string; title: string; validUntil: string | null } | null;
  note: string | null;
}

/** Per gebouw: soort toch verplicht, niet van toepassing, of terug naar het voorstel (null). */
export const dossierRequirementSchema = z.object({
  state: z.enum(["required", "not_applicable"]).nullable(),
  note: optionalText(300),
});

/** Overzicht Dossiers over alle gebouwen. */
export interface DossierSummaryDto {
  building: { id: string; code: string; name: string; city: string | null };
  usageFunctions: UsageFunction[];
  documents: number;
  missing: number;
  expiring: number;
  expired: number;
}

/** Documenten bij een asset: direct gekoppeld of via de hoofdasset. */
export interface AssetDocumentDto extends DocumentDto {
  via: { id: string; code: string; name: string } | null;
}

export const DOC_EXT_LABEL = (name: string) => (name.split(".").pop() ?? "").toUpperCase().slice(0, 4);

// ---------- deellink ----------

export const SHARE_DAYS = [1, 7, 30, 90] as const;
export const documentShareSchema = z.object({
  days: z.union([z.literal(1), z.literal(7), z.literal(30), z.literal(90)]).default(7),
  note: optionalText(120),
  /** Alleen deze versie delen; anders opent de link steeds de nieuwste versie. */
  fixedVersion: z.boolean().default(false),
});

export interface DocumentShareDto {
  id: string;
  note: string | null;
  version: number | null;
  expiresAt: string;
  revokedAt: string | null;
  openCount: number;
  lastOpenedAt: string | null;
  createdBy: string | null;
  createdAt: string;
}

/** Wat de ontvanger van een deellink ziet (zonder inloggen). */
export interface PublicDocumentDto {
  org: string;
  building: string;
  title: string;
  typeName: string;
  fileName: string;
  mime: string;
  size: number | null;
  version: number;
  revision: string | null;
  validUntil: string | null;
  expiresAt: string;
}

// ---------- markeringen (annoteren zonder nieuwe revisie) ----------

export const ANNOTATION_COLORS = ["rood", "blauw", "groen"] as const;
export type AnnotationColor = (typeof ANNOTATION_COLORS)[number];
export const ANNOTATION_COLOR_HEX: Record<AnnotationColor, string> = {
  rood: "#d92d20",
  blauw: "#1570ef",
  groen: "#067647",
};

/** Coördinaten genormaliseerd (0–1) op het blad zoals getoond, oorsprong linksboven. */
const unit = z.number().min(0).max(1);
const point = z.tuple([unit, unit]);
export const annotationShapeSchema = z.discriminatedUnion("kind", [
  /** Vrije lijn (pen). */
  z.object({ kind: z.literal("pen"), points: z.array(point).min(2).max(4000) }),
  /** Rechte lijnstukken; gesloten = vlak (polygoon). */
  z.object({ kind: z.literal("line"), points: z.array(point).min(2).max(200), closed: z.boolean().default(false) }),
  /** Revisiewolk om een gewijzigd deel. */
  z.object({ kind: z.literal("cloud"), x: unit, y: unit, w: unit, h: unit }),
  /** Tekst op het blad. */
  z.object({ kind: z.literal("text"), x: unit, y: unit, text: z.string().trim().min(1).max(500) }),
]);
export type AnnotationShape = z.infer<typeof annotationShapeSchema>;

export const annotationSchema = z.object({
  page: z.number().int().min(1).max(2000),
  color: z.enum(ANNOTATION_COLORS).default("rood"),
  shape: annotationShapeSchema,
});
export type AnnotationInput = z.infer<typeof annotationSchema>;

export interface AnnotationDto extends AnnotationInput {
  id: string;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}
